import React, { useCallback, useEffect, useRef, useState } from "react";
import _ from "lodash";
import { FaTimes } from "react-icons/fa";
import { Button, ButtonGroup, Form } from "react-bootstrap";

const normalizeText = (text) =>
  text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const SelectAutocomplete = ({
  options,
  optionValue = "value",
  optionLabel = "label",
  maxOptions = 10,
  disabled,
  error,
  value,
  onChange = (selectedValue) => {
    console.log(selectedValue);
  },
}) => {
  const [optimizedOptions, setOptimizedOptions] = useState([]);
  const [initialValue] = useState(value);
  const [filteredOptions, setFilteredOptions] = useState([...optimizedOptions]);
  const [argument, setArgument] = useState("");
  const [showList, setShowList] = useState(false);
  const inputTextRef = useRef();
  const listRef = useRef();
  const onKeyDownListenerRef = useRef((e) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      listRef.current?.focus();
      e.preventDefault();
    }
  });

  useEffect(() => {
    setOptimizedOptions(() =>
      options.map((item) => ({
        [optionValue]: item[optionValue],
        [optionLabel]: item[optionLabel],
        [`${optionLabel}Localized`]: normalizeText(item[optionLabel]),
      })),
    );
  }, [options, optionValue, optionLabel]);

  const filterHandler = useCallback(
    (arg) => {
      const argumentLocalized = normalizeText(arg);
      setFilteredOptions(
        _.take(
          optimizedOptions.filter((item) => item[`${optionLabel}Localized`].indexOf(argumentLocalized) > -1),
          maxOptions,
        ),
      );
    },
    [optimizedOptions, optionLabel, maxOptions],
  );

  useEffect(() => {
    setArgument(() => {
      const arg = (optimizedOptions.find((item) => item[optionValue] === initialValue) || {})[optionLabel] ?? "";
      filterHandler(arg);
      return arg;
    });
  }, [filterHandler, optimizedOptions, initialValue, optionLabel, optionValue]);

  const clear = () => {
    setShowList(false);
    onChange(initialValue);
    setArgument((optimizedOptions.find((item) => item[optionValue] === initialValue) || {})[optionLabel] ?? "");
    inputTextRef.current.focus();
  };

  const getSelectedValue = () => {
    const selectedItem = _.find(
      optimizedOptions,
      (item) => `${item[optionValue]}` === listRef.current.selectedOptions[0].value,
    );
    return selectedItem ? selectedItem[optionValue] : undefined;
  };

  const select = () => {
    setShowList(false);
    if (listRef.current?.selectedOptions?.length) {
      setArgument(listRef.current.selectedOptions[0].innerText);
      onChange(getSelectedValue());
      inputTextRef.current.focus();
    } else if (listRef.current && listRef.current.selectedOptions.length === 0) {
      clear();
    }
  };

  return (
    <>
      <ButtonGroup className="w-100">
        <Form.Control
          ref={inputTextRef}
          type="text"
          disabled={disabled}
          isInvalid={!!error}
          value={argument}
          onChange={(e) => {
            setShowList(true);
            setArgument(e.target.value);
            filterHandler(e.target.value);
          }}
          onFocus={() => {
            document.addEventListener("keydown", onKeyDownListenerRef.current);
          }}
          onBlur={(e) => {
            document.removeEventListener("keydown", onKeyDownListenerRef.current);
            if (e.relatedTarget !== listRef.current) {
              setShowList(false);
              select();
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              select();
            } else if (e.key === "Escape") {
              clear();
            }
          }}
          onClick={() => setShowList((curr) => !curr)}
        />
        <Button
          className="btn-search-inside btn-md"
          onClick={() => {
            clear();
          }}
        >
          <FaTimes />
        </Button>
      </ButtonGroup>

      {!!error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}

      {showList && (
        <div>
          <Form.Control
            ref={listRef}
            as="select"
            multiple={false}
            htmlSize={maxOptions}
            style={{
              position: "absolute",
              zIndex: 999,
              overflow: "overlay",
            }}
            disabled={false}
            value={value}
            onChange={(e) => {
              setArgument(e.target.selectedOptions[0].innerText);
              // formik.setFieldValue(field, e.target.selectedOptions[0].value);
              onChange(getSelectedValue());
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                select();
              } else if (e.key === "Escape") {
                clear();
              }
            }}
            onClick={() => {
              select();
            }}
            onBlur={(e) => {
              if (e.relatedTarget !== inputTextRef.current) {
                setShowList(false);
                select();
              }
            }}
          >
            {(filteredOptions || []).map((item) => (
              <option key={item[optionValue]} value={item[optionValue]}>
                {item[optionLabel]}
              </option>
            ))}
          </Form.Control>
        </div>
      )}
    </>
  );
};

export default SelectAutocomplete;
